/**
 * Computed-tags store — React Context holding the per-tenant list of
 * `ComputedTagDef`s plus CRUD actions. Mirrors the `PlantProvider`
 * pattern in `apps/web/src/lib/plant/plant-store.tsx`.
 *
 * Stub mode seeds from `FX_COMPUTED_TAGS`; live mode reads and writes
 * `/v1/iot/computed-tags` (#165 — before that, live mode was permanently empty,
 * so every server-side computed tag was invisible in the product and anything
 * authored here vanished on reload).
 *
 * Writes follow the alarms-store idiom (`lib/alarms/store.tsx`): optimistic local
 * insert first — which is what keeps `add()`/`duplicate()` synchronous for the
 * copilot tools — then POST and refetch to reconcile with the server-minted row,
 * rolling the optimistic row back out on failure.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FX_COMPUTED_TAGS } from "./__fixtures";
import { isLiveApi } from "@/lib/api";
import { COMPUTED_TAGS_KEY, useComputedTagsQuery } from "@/lib/api/queries";
import {
  computedTags as computedTagsApi,
  toComputedTagBody,
  toComputedTagDef,
} from "@/lib/api/computed-tags";
import { backendErrorMessage } from "@/lib/api/error";
import { useToast } from "@/lib/ui/toast";
import type { ComputedTagDef } from "./types";

export interface ComputedTagsState {
  tags: ComputedTagDef[];
  /** Look up by id. */
  byId: (id: string) => ComputedTagDef | undefined;
  /** Resolve an in-expression alias to a tag id, given a known def. */
  resolveAlias: (defId: string, alias: string) => string | undefined;
  add: (def: Omit<ComputedTagDef, "id" | "createdAt" | "updatedAt">) => string;
  update: (id: string, patch: Partial<ComputedTagDef>) => void;
  remove: (id: string) => void;
  duplicate: (id: string) => string | undefined;
  /** Reset to fixture seed (useful for demos + tests). */
  reset: () => void;
  /** Drives memoised consumers like dashboards' alias picker. */
  version: number;
}

const ComputedTagsContext = createContext<ComputedTagsState | null>(null);

export function ComputedTagsProvider({ children }: { children: ReactNode }) {
  // Live mode starts empty and is filled by the query below; stub mode uses
  // fixtures (they'd otherwise show the same demo computed tags in every org).
  const [tags, setTags] = useState<ComputedTagDef[]>(
    isLiveApi ? [] : FX_COMPUTED_TAGS,
  );
  const [version, setVersion] = useState(0);

  const query = useComputedTagsQuery();
  const toast = useToast();
  const qc = useQueryClient();
  const erroredRef = useRef(false);

  const bump = useCallback(() => setVersion((v) => v + 1), []);

  /** Re-pull after a write so the list reflects server truth (real ids, defaults). */
  const refetch = useCallback(
    () => void qc.invalidateQueries({ queryKey: COMPUTED_TAGS_KEY }),
    [qc],
  );

  useEffect(() => {
    if (query.data) {
      setTags(query.data.items.map(toComputedTagDef));
      bump();
    }
  }, [query.data, bump]);

  useEffect(() => {
    if (query.error && !erroredRef.current) {
      erroredRef.current = true;
      toast.push("error", backendErrorMessage(query.error, "couldn't load computed tags"));
    }
  }, [query.error, toast]);

  const byId = useCallback(
    (id: string) => tags.find((t) => t.id === id),
    [tags],
  );

  const resolveAlias = useCallback(
    (defId: string, alias: string): string | undefined => {
      const def = tags.find((t) => t.id === defId);
      return def?.inputs.find((i) => i.alias === alias)?.tagId;
    },
    [tags],
  );

  /**
   * Optimistic local insert, then POST + refetch. The synchronous `id` return is
   * load-bearing — `lib/copilot/tools.ts` / `dispatch.ts` hand it straight to
   * `recordAudit` for Undo. In live mode that id is a CLIENT-minted placeholder
   * until the refetch swaps in the server row, so an Undo replayed from a later
   * session (audit entries persist to localStorage) may no longer resolve. That
   * is pre-existing and orthogonal; wiring the store doesn't make it worse.
   */
  const add = useCallback(
    (def: Omit<ComputedTagDef, "id" | "createdAt" | "updatedAt">): string => {
      const id = `ct-${def.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${Date.now()}`;
      const now = new Date().toISOString();
      const next: ComputedTagDef = {
        ...def,
        id,
        createdAt: now,
        updatedAt: now,
      };
      setTags((prev) => [...prev, next]);
      bump();
      if (isLiveApi) {
        void computedTagsApi
          .create(toComputedTagBody(def))
          .then(() => refetch())
          .catch((err) => {
            setTags((prev) => prev.filter((t) => t.id !== id));
            bump();
            toast.push("error", backendErrorMessage(err, "computed tag not saved"));
          });
      }
      return id;
    },
    [bump, refetch, toast],
  );

  const update = useCallback(
    (id: string, patch: Partial<ComputedTagDef>) => {
      let merged: ComputedTagDef | undefined;
      setTags((prev) =>
        prev.map((t) => {
          if (t.id !== id) return t;
          merged = { ...t, ...patch, updatedAt: new Date().toISOString() };
          return merged;
        }),
      );
      bump();
      if (isLiveApi && merged) {
        void computedTagsApi
          .update(id, toComputedTagBody(merged))
          .then(() => refetch())
          .catch((err) => {
            refetch(); // drop the optimistic edit; server truth wins
            toast.push("error", backendErrorMessage(err, "computed tag not updated"));
          });
      }
    },
    [bump, refetch, toast],
  );

  const remove = useCallback(
    (id: string) => {
      const snapshot = tags;
      setTags((prev) => prev.filter((t) => t.id !== id));
      bump();
      if (isLiveApi) {
        void computedTagsApi
          .remove(id)
          .then(() => refetch())
          .catch((err) => {
            setTags(snapshot); // put it back — the server still has it
            bump();
            toast.push("error", backendErrorMessage(err, "computed tag not deleted"));
          });
      }
    },
    [tags, bump, refetch, toast],
  );

  const duplicate = useCallback(
    (id: string): string | undefined => {
      const src = tags.find((t) => t.id === id);
      if (!src) return undefined;
      // Names must stay distinct: every org computed tag lands on the ONE per-org
      // `_computed` device and there is no unique index on tags(thing_id, name),
      // so two "X (copy)" rows would silently share one readings series.
      const taken = new Set(tags.map((t) => t.name));
      let name = `${src.name} (copy)`;
      for (let n = 2; taken.has(name); n++) name = `${src.name} (copy ${n})`;
      const { id: _id, createdAt: _c, updatedAt: _u, ...rest } = src;
      return add({ ...rest, name });
    },
    [tags, add],
  );

  /** Stub mode: back to the fixture seed. Live mode: re-pull server truth. */
  const reset = useCallback(() => {
    if (isLiveApi) {
      refetch();
      return;
    }
    setTags(FX_COMPUTED_TAGS);
    bump();
  }, [refetch, bump]);

  const value = useMemo<ComputedTagsState>(
    () => ({ tags, byId, resolveAlias, add, update, remove, duplicate, reset, version }),
    [tags, byId, resolveAlias, add, update, remove, duplicate, reset, version],
  );

  return (
    <ComputedTagsContext.Provider value={value}>
      {children}
    </ComputedTagsContext.Provider>
  );
}

export function useComputedTags(): ComputedTagsState {
  const ctx = useContext(ComputedTagsContext);
  if (!ctx) {
    throw new Error("useComputedTags must be used inside <ComputedTagsProvider>");
  }
  return ctx;
}

/**
 * Hook-friendly variant that returns an empty state instead of throwing
 * when used outside the provider. Useful from libraries (dashboard,
 * alarms) that should still render if the provider isn't mounted —
 * computed tags simply don't appear in their pickers.
 */
export function useOptionalComputedTags(): ComputedTagsState | null {
  return useContext(ComputedTagsContext);
}
